"use client";

import { SchedulingGuestPhoneField } from "@/app/(public)/_components/scheduling-guest-phone-field";
import { useLocaleTag } from "@/lib/i18n/format";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import {
  type DemoFormErrors,
  type DemoFormValues,
  T,
  formatUnits,
} from "./demo-call-shared";

const labelStyle: CSSProperties = {
  fontFamily: T.label,
  fontSize: 10,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
  color: T.inkFaint,
  marginBottom: 8,
};

function MonoLabel({
  children,
  htmlFor,
}: {
  readonly children: ReactNode;
  readonly htmlFor?: string;
}): ReactNode {
  return (
    <label htmlFor={htmlFor} style={labelStyle}>
      {children}
    </label>
  );
}

type TextFieldProps = {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: "text" | "email" | "tel" | "url";
  readonly inputMode?: "text" | "email" | "tel" | "url";
  readonly autoComplete?: string;
  readonly error?: string | undefined;
};

export function TextField({
  id,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  inputMode,
  autoComplete,
  error,
}: TextFieldProps): ReactNode {
  return (
    <div style={{ display: "block" }}>
      <div style={{ display: "block" }}>
        <MonoLabel htmlFor={id}>{label}</MonoLabel>
      </div>
      <input
        id={id}
        name={id}
        className="mkdemo-input"
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        aria-invalid={error ? "true" : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <div style={{ color: "#B24A30", fontSize: 12, marginTop: 6 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function UnitsSlider({
  units,
  onChange,
}: {
  readonly units: number;
  readonly onChange: (value: number) => void;
}): ReactNode {
  const localeTag = useLocaleTag();
  const t = useTranslations("public.demoCallFields");
  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 11,
        }}
      >
        <span style={{ ...labelStyle, marginBottom: 0 }}>
          {t("anzahlEinheiten")}
        </span>
        <span
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: T.accent,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatUnits(units, localeTag)}
        </span>
      </div>
      <input
        className="mkdemo-range"
        type="range"
        min={0}
        max={10000}
        step={100}
        value={units}
        aria-label={t("anzahlEinheiten")}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: "rgba(11,15,25,0.4)",
          marginTop: 6,
        }}
      >
        <span>0</span>
        <span>{t("k10000")}</span>
      </div>
    </div>
  );
}

/** The full set of demo-form fields. */
export function DemoFormBody({
  values,
  errors,
  onChange,
  onPhoneValidChange,
}: {
  readonly values: DemoFormValues;
  readonly errors: DemoFormErrors;
  readonly onChange: <K extends keyof DemoFormValues>(
    key: K,
    value: DemoFormValues[K],
  ) => void;
  readonly onPhoneValidChange?: (valid: boolean) => void;
}): ReactNode {
  const t = useTranslations("public.demoCallFields");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <MonoLabel>{t("name")}</MonoLabel>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              id="firstName"
              name="firstName"
              className="mkdemo-input"
              type="text"
              autoComplete="given-name"
              placeholder={t("vorname")}
              value={values.firstName}
              aria-invalid={errors.firstName ? "true" : undefined}
              onChange={(e) => onChange("firstName", e.target.value)}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              id="lastName"
              name="lastName"
              className="mkdemo-input"
              type="text"
              autoComplete="family-name"
              placeholder={t("nachname")}
              value={values.lastName}
              onChange={(e) => onChange("lastName", e.target.value)}
            />
          </div>
        </div>
        {errors.firstName ? (
          <div style={{ color: "#B24A30", fontSize: 12, marginTop: 6 }}>
            {errors.firstName}
          </div>
        ) : null}
      </div>

      <TextField
        id="company"
        label={t("firmenname")}
        placeholder={t("hausverwaltung")}
        autoComplete="organization"
        value={values.company}
        onChange={(v) => onChange("company", v)}
      />

      <TextField
        id="website"
        label={t("webseite")}
        placeholder={t("wwwVerwaltungDe")}
        type="url"
        inputMode="url"
        autoComplete="url"
        value={values.website}
        error={errors.website}
        onChange={(v) => onChange("website", v)}
      />

      <TextField
        id="email"
        label={t("eMailAdresse")}
        placeholder={t("nameFirmaDe")}
        type="email"
        inputMode="email"
        autoComplete="email"
        value={values.email}
        error={errors.email}
        onChange={(v) => onChange("email", v)}
      />

      <SchedulingGuestPhoneField
        value={values.phone}
        onChange={(v) => onChange("phone", v)}
        required
        onValidChange={onPhoneValidChange}
        forceShowError={errors.phone !== undefined}
      />

      <UnitsSlider
        units={values.units}
        onChange={(v) => onChange("units", v)}
      />
    </div>
  );
}
